import { defineStore, storeToRefs } from 'pinia'
import { computed, type Raw, ref, shallowRef, watch } from 'vue'

import { useConfig } from '@/composables/config'
import { APP_NATIVE_AD_CLIDS } from '@/constants/general'
import { AdService } from '@/modules/adv/ad-service'
import { type ClidAccountOverridePayload, resolveClidAccountOverride } from '@/modules/adv/clid-account-override'
import { ConsentManager, type ConsentUiState } from '@/modules/adv/consent-manager'
import { HeaderBidding } from '@/modules/adv/header-bidding/prebid'
import type { HbSiteGameInfo, HeaderBiddingOptions } from '@/modules/adv/header-bidding/prebid/types'
import { initProviders } from '@/modules/adv/provider-initializer'
import { TargetingMapping } from '@/modules/adv/targeting-mapping'
import type { PortalAdTargeting } from '@/modules/adv/types'
import { LogLevel, setAdConsoleLoggerLevel } from '@/modules/adv/utils/ad-console-logger'
import { addAdsPanel } from '@/modules/devtools/panels/ads/ads-panel'
import { SSR_QUERY } from '@/router/query-params'
import { useAdBlockStore } from '@/store/ad-block-store'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { getHashedEmailFromCookie, updateCookie, YAHOO_ID_SIGNAL_COOKIE } from '@/utils/cookie-parser'
import { sha256hex } from '@/utils/crypto'
import { getEmbeddingHostname } from '@/utils/embedding-host'
import { transformUACtoAdTargetings } from '@/utils/uac-cookie'

import { useNativeAppStore } from '../native-app-store'
import { getProvidersSettings } from './providers-settings'

const NATIVE_CONSENT_WAIT_MIN_VERSIONS = { ios: '1.6.0', android: '1.6.0' }

export const useAdsStore = defineStore('ads', () => {
    const { logEvent, startRumEvent, registerPageHideFallbackEvent } = useLoggerStore()
    const appStore = useAppStore()
    const nativeAppStore = useNativeAppStore()
    const clientAppStore = useClientAppStore()
    const {
        clid,
        platform,
        isDebugMode,
        isGameDistribution,
        isPartnerEmbed,
        isNativeAppEnv,
        adExpTargeting,
        countryCode,
        isH5RewardedAuction,
    } = storeToRefs(appStore)
    const { adBlockStatus } = storeToRefs(useAdBlockStore())

    const nativeBannersEnabled = computed(
        () => appStore.isVersionAtLeast({ android: '1.3.0', ios: '1.3.0' }) && !!APP_NATIVE_AD_CLIDS[clid.value],
    )

    const userStore = useUserStore()

    const adService = shallowRef<Raw<AdService>>()
    const headerBidding = shallowRef<Raw<HeaderBidding>>()
    const consentManager = shallowRef<Raw<ConsentManager>>()
    const consentUiState = ref<ConsentUiState>('unknown')
    let unsubscribeConsentUiState: (() => void) | undefined
    let pendingGameInfo: HbSiteGameInfo | null = null

    const { VUE_APP_AD_MANAGER_ACCOUNT, VUE_APP_SSP_HOST, VUE_APP_HOST_URL, VUE_APP_ADS_TURNSTILE_SITE_KEY, E2E } =
        useConfig()

    function waitForNativeConsent(): Promise<void> {
        return new Promise((resolve) => {
            const unsubscribe = nativeAppStore.addMessageHandler((data) => {
                if ((data as { type?: string } | null)?.type !== 'native:consent.event') {
                    return
                }
                unsubscribe()
                resolve()
            })
        })
    }

    function setAdServiceConfig(options: {
        targeting?: PortalAdTargeting
        auid: string
        isGoogleProvidersEnabled: boolean
        clidAccountOverride?: ClidAccountOverridePayload | null
        gameDistributionGameId?: string
    }) {
        const targeting: PortalAdTargeting = {
            playgama_clid: clid.value,
            widget_id: clientAppStore.widgetId || '',
            event_source: 'default',
            exp: adExpTargeting.value,
            h5_rewarded_auction: isH5RewardedAuction.value ? 'on' : 'off',
            [TargetingMapping.AD_BLOCK_STATUS]: adBlockStatus.value,
            [TargetingMapping.IS_LOGGED_IN]: '0',
            ...transformUACtoAdTargetings(clientAppStore.userAcquisitionParams),
            ...options.targeting,
        }

        const headerBiddingConfig: HeaderBiddingOptions = {
            debug: isDebugMode.value,
            apsDebug: appStore.initialQuery[SSR_QUERY.DEBUG] === 'aps',
            platform: platform.value,
            pgAuid: clientAppStore.auid,
            countryCode: countryCode.value,
            adSource: isNativeAppEnv.value ? 'native' : 'portal',
            pubProvidedStype: 'ppuid',
            sspHost: VUE_APP_SSP_HOST,
            renderersBase: `${VUE_APP_HOST_URL}/prebid-renderers`,
            hashedEmail: getHashedEmailFromCookie(),
            logEvent,
        }

        const embeddingHostname = isPartnerEmbed.value ? getEmbeddingHostname() : ''
        const gamSubAccount =
            options.clidAccountOverride &&
            resolveClidAccountOverride(options.clidAccountOverride, embeddingHostname, { clid: clid.value, logEvent })
                .gamSubAccount
        const gamChildNetwork = gamSubAccount ? { gamSubAccount, pageDomain: embeddingHostname } : null

        const currentProvidersSettings = getProvidersSettings({
            countryCode: countryCode.value,
            isPlaygamaBidderOnly: E2E,
            gamAccount: VUE_APP_AD_MANAGER_ACCOUNT,
            appNativeSDK: nativeAppStore.sdk,
            platform: platform.value,
            effectiveType: clientAppStore.effectiveType,
            isVersionAtLeast: appStore.isVersionAtLeast,
            isNativeBannersEnabled: nativeBannersEnabled.value,
            gameDistributionGameId: options.gameDistributionGameId,
            clid: clid.value,
            isExportOnWidget: appStore.isExportOnWidget,
            isGameDistribution: isGameDistribution.value,
            isPartnerEmbed: isPartnerEmbed.value,
            isNativeAppEnv: isNativeAppEnv.value,
            isGoogleProvidersEnabled: options.isGoogleProvidersEnabled,
            isH5RewardedAuction: isH5RewardedAuction.value,
            gamChildNetwork,
        })

        if (headerBiddingConfig && isPartnerEmbed.value) {
            headerBiddingConfig.adSource = 'export'
        }

        const isServedByApp = nativeBannersEnabled.value
        const waitsForNativeConsent = isServedByApp && appStore.isVersionAtLeast(NATIVE_CONSENT_WAIT_MIN_VERSIONS)

        consentManager.value = new ConsentManager({
            handleConsentlessEnv: () => Promise.resolve(countryCode.value === 'US'),
            consentAccount: VUE_APP_AD_MANAGER_ACCOUNT,
            skipCmp: isServedByApp,
            nativeConsentReady: waitsForNativeConsent ? waitForNativeConsent() : undefined,
            logEvent,
        })

        adService.value = new AdService({
            targeting,
            startRumEvent,
            logEvent,
            registerPageHideFallbackEvent,
            auid: options.auid,
            waitForHeaderBidding: !isServedByApp,
            consentReady: consentManager.value.ready,
            skipReadyTimeout: waitsForNativeConsent,
            providersSettings: currentProvidersSettings.map(({ placements, settings }) => ({
                placements,
                settings: settings.map((s) => ({ type: s.type, timeout: s.options.timeout })),
            })),
            useFallbackAuctionReuse: true,
            turnstileSiteKey: VUE_APP_ADS_TURNSTILE_SITE_KEY,
        })

        addAdsPanel({
            getAdService: () => adService.value,
            context: () => ({
                title: 'Portal',
                rows: [
                    { label: 'Header bidding ad source', value: headerBiddingConfig.adSource },
                    { label: 'Native banners in the mobile app', value: isServedByApp },
                    { label: 'Header bidding set up', value: !!headerBidding.value },
                    { label: 'Consent source', value: consentManager.value?.consentSource },
                    { label: 'Ad blocker', value: adBlockStatus.value },
                    { label: 'Country', value: countryCode.value },
                    { label: 'Platform', value: platform.value },
                    { label: 'clid', value: clid.value },
                    { label: 'auid', value: options.auid, copy: true },
                    { label: 'Embedding host', value: embeddingHostname },
                    { label: 'GAM child network', value: gamSubAccount },
                ],
            }),
        })

        unsubscribeConsentUiState?.()
        unsubscribeConsentUiState = consentManager.value.onConsentUiStateChange((state) => {
            consentUiState.value = state
        })

        consentManager.value.install().then((activeCmps) => {
            if (isServedByApp) {
                return
            }
            const hb = new HeaderBidding(headerBiddingConfig, activeCmps, startRumEvent)
            headerBidding.value = hb
            hb.updateGameInfo(pendingGameInfo)
            adService.value?.setHeaderBidding(hb)
        })

        userStore.getUser().then(async () => {
            if (userStore.isCompanyEmployeeLoggedIn) {
                setAdConsoleLoggerLevel(LogLevel.debug)
            }
            if (userStore.user?.email && userStore.user?.email !== '') {
                const email = await sha256hex(userStore.user.email)
                updateCookie(YAHOO_ID_SIGNAL_COOKIE, email, 1000 * 50 * 60 * 24 * 30) // set cookie for 30 days
            }
        })

        consentManager.value.ready.then(() => {
            currentProvidersSettings.forEach(({ placements, settings }) => {
                initProviders(settings, { targeting, startRumEvent, auid: options.auid }, (index, provider) => {
                    placements.forEach((placement) => adService.value?.setProvider(index, provider, placement))
                })
            })
        })
    }

    function updateTargeting(targeting: PortalAdTargeting) {
        adService.value?.updateTargeting(targeting)
    }

    function updateHbGameInfo(gameInfo: HbSiteGameInfo | null) {
        pendingGameInfo = gameInfo
        headerBidding.value?.updateGameInfo(gameInfo)
    }

    watch(adBlockStatus, (status) => {
        updateTargeting({
            [TargetingMapping.AD_BLOCK_STATUS]: status,
        })
    })

    return {
        adService,
        consentManager,
        consentUiState,
        headerBidding,
        nativeBannersEnabled,
        setAdServiceConfig,
        updateTargeting,
        updateHbGameInfo,
    }
})
